import type {
  AggregationResultsCategoryRow,
  ExternalResourceAspect,
  ExternalResourceCategory,
} from '../../../../../store/api/monitoring-api';

export type PyramidCategory = { id: string; name: string };
export type PyramidAspect = {
  categoryId: string;
  name: string;
  positive: number;
  negative: number;
};

export type ExternalMonitoringKpiHighlight = {
  name: string;
  id: string;
  positive: number;
  neutral: number;
  negative: number;
};

export type ExternalMonitoringKpiPayload = {
  totalComments: number;
  aspectsCount: number;
  positive: number;
  neutral: number;
  negative: number;
  sentimentIndex: number;
  driver: ExternalMonitoringKpiHighlight | null;
  risk: ExternalMonitoringKpiHighlight | null;
};

export type HeatmapAspectBreakdownRow = {
  aspectId: number;
  name: string;
  negative: number;
  neutral: number;
  positive: number;
};

export type ExternalMonitoringChartsPayload = {
  sentimentDynamics: {
    dates: string[];
    positive: number[];
    negative: number[];
  };
  heatmap: {
    categories: string[];
    categoryIds: string[];
    /** [негатив, нейтрал, позитив] — количества по строке категории */
    counts: [number, number, number][];
    aspectsByCategoryId: Record<string, HeatmapAspectBreakdownRow[]>;
  };
  pyramid: {
    categories: PyramidCategory[];
    aspects: PyramidAspect[];
  };
  aspectsDynamics: {
    dates: string[];
    categories: Array<{
      id: number;
      name: string;
      negative: number;
      neutral: number;
      positive: number;
    }>;
  };
};

function categoryName(categoryId: number, categoryMap: Map<number, string>): string {
  return categoryMap.get(categoryId) ?? `Категория ${categoryId}`;
}

function aspectName(aspectId: number, aspectMap: Map<number, string>): string {
  return aspectMap.get(aspectId) ?? `Аспект ${aspectId}`;
}

export function aggregateExternalMonitoringDashboard(
  rows: AggregationResultsCategoryRow[],
  categories: ExternalResourceCategory[],
  aspects: ExternalResourceAspect[],
  periodLabel: string,
): { kpi: ExternalMonitoringKpiPayload; charts: ExternalMonitoringChartsPayload } {
  const categoryMap = new Map(categories.map((c) => [c.id, c.name]));
  const aspectMap = new Map(aspects.map((a) => [a.id, a.name]));

  let positive = 0;
  let neutral = 0;
  let negative = 0;

  type AspectAgg = {
    aspectId: number;
    categoryId: number;
    positive: number;
    negative: number;
    neutral: number;
  };
  type CategoryAgg = { positive: number; negative: number; neutral: number };
  type CatAspectAgg = { aspectId: number; positive: number; negative: number; neutral: number };
  const byAspect = new Map<number, AspectAgg>();
  const byCategory = new Map<number, CategoryAgg>();
  const byCategoryAspect = new Map<string, CatAspectAgg>();
  let aspectCommentsCountSum = 0;

  rows.forEach((row) => {
    const cid = Number(row.category_id);
    (row.aspects || []).forEach((a) => {
      aspectCommentsCountSum += Number(a.comments_count || 0);
      const aid = Number(a.aspect_id);
      const p = Number(a.positive_count || 0);
      const neu = Number(a.neutral_count || 0);
      const neg = Number(a.negative_count || 0);
      positive += p;
      neutral += neu;
      negative += neg;

      const cur = byAspect.get(aid);
      if (!cur) {
        byAspect.set(aid, {
          aspectId: aid,
          categoryId: cid,
          positive: p,
          negative: neg,
          neutral: neu,
        });
      } else {
        cur.positive += p;
        cur.negative += neg;
        cur.neutral += neu;
      }

      const caKey = `${cid}:${aid}`;
      const ca = byCategoryAspect.get(caKey) ?? {
        aspectId: aid,
        positive: 0,
        negative: 0,
        neutral: 0,
      };
      ca.positive += p;
      ca.negative += neg;
      ca.neutral += neu;
      byCategoryAspect.set(caKey, ca);

      const cat = byCategory.get(cid) ?? { positive: 0, negative: 0, neutral: 0 };
      cat.positive += p;
      cat.negative += neg;
      cat.neutral += neu;
      byCategory.set(cid, cat);
    });
  });

  const aspectsCount = byAspect.size;

  /** Для KPI используем комментарийную базу только из аспектов */
  const totalComments = aspectCommentsCountSum;

  const rawSentimentIndex =
    totalComments > 0 ? ((positive - negative) / totalComments) * 100 : 0;
  const sentimentIndex = Math.max(-100, Math.min(100, rawSentimentIndex));

  const uniqueCategoryIds = [...new Set(rows.map((r) => Number(r.category_id)))].filter((id) =>
    Number.isFinite(id),
  );
  const isSingleCategoryScope = uniqueCategoryIds.length <= 1;

  let driver: ExternalMonitoringKpiPayload['driver'] = null;
  let risk: ExternalMonitoringKpiPayload['risk'] = null;

  type KpiCandidate = {
    id: string;
    name: string;
    positive: number;
    neutral: number;
    negative: number;
  };
  const pickDriverAndRisk = (candidates: KpiCandidate[]) => {
    if (!candidates.length) {
      return {
        driver: null as ExternalMonitoringKpiPayload['driver'],
        risk: null as ExternalMonitoringKpiPayload['risk'],
      };
    }

    const positiveCandidates = candidates.filter((item) => item.positive > item.negative);
    const negativeCandidates = candidates.filter((item) => item.negative > item.positive);

    const sortedByPositive = [...positiveCandidates].sort(
      (a, b) => b.positive - a.positive || a.negative - b.negative,
    );
    const sortedByNegative = [...negativeCandidates].sort(
      (a, b) => b.negative - a.negative || a.positive - b.positive,
    );

    const bestDriver = sortedByPositive[0] || null;
    const bestRisk = sortedByNegative[0] || null;

    if (!bestDriver && !bestRisk) {
      return {
        driver: null as ExternalMonitoringKpiPayload['driver'],
        risk: null as ExternalMonitoringKpiPayload['risk'],
      };
    }

    return {
      driver: bestDriver,
      risk: bestRisk,
    };
  };

  if (isSingleCategoryScope) {
    // При выборе конкретной категории показываем аспекты внутри неё.
    const candidates: KpiCandidate[] = [];
    for (const [aspectId, agg] of byAspect) {
      const weight = agg.positive + agg.negative;
      if (weight <= 0) continue;
      candidates.push({
        name: aspectName(aspectId, aspectMap),
        id: String(aspectId),
        positive: agg.positive,
        neutral: agg.neutral,
        negative: agg.negative,
      });
    }
    const picked = pickDriverAndRisk(candidates);
    driver = picked.driver;
    risk = picked.risk;
  } else {
    // На общем экране (все категории) показываем категории.
    const candidates: KpiCandidate[] = [];
    for (const [categoryId, agg] of byCategory) {
      candidates.push({
        name: categoryName(categoryId, categoryMap),
        id: String(categoryId),
        positive: agg.positive,
        neutral: agg.neutral,
        negative: agg.negative,
      });
    }
    const picked = pickDriverAndRisk(candidates);
    driver = picked.driver;
    risk = picked.risk;
  }

  const kpi: ExternalMonitoringKpiPayload = {
    totalComments,
    aspectsCount,
    positive,
    neutral,
    negative,
    sentimentIndex,
    driver,
    risk,
  };

  /** Одна строка на категорию: количества по тональностям (проценты считаются на клиенте с округлением до 100%). */
  const heatmapCategories: string[] = [];
  const heatmapCategoryIds: string[] = [];
  const heatmapCounts: [number, number, number][] = [];
  for (const id of uniqueCategoryIds) {
    const c = byCategory.get(id) ?? { positive: 0, negative: 0, neutral: 0 };
    heatmapCategories.push(categoryName(id, categoryMap));
    heatmapCategoryIds.push(String(id));
    heatmapCounts.push([c.negative, c.neutral, c.positive]);
  }

  const aspectsByCategoryId: Record<string, HeatmapAspectBreakdownRow[]> = {};
  for (const [key, ca] of byCategoryAspect) {
    const cidStr = key.split(':')[0];
    if (!aspectsByCategoryId[cidStr]) aspectsByCategoryId[cidStr] = [];
    const aid = ca.aspectId;
    aspectsByCategoryId[cidStr].push({
      aspectId: aid,
      name: aspectName(aid, aspectMap),
      negative: ca.negative,
      neutral: ca.neutral,
      positive: ca.positive,
    });
  }
  for (const k of Object.keys(aspectsByCategoryId)) {
    aspectsByCategoryId[k].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }
  const pyramidCategories: PyramidCategory[] = uniqueCategoryIds.map((id) => ({
    id: String(id),
    name: categoryName(id, categoryMap),
  }));

  const pyramidAspects: PyramidAspect[] = [];
  rows.forEach((row) => {
    const cid = String(row.category_id);
    (row.aspects || []).forEach((a) => {
      const aid = Number(a.aspect_id);
      pyramidAspects.push({
        categoryId: cid,
        name: aspectName(aid, aspectMap),
        positive: Number(a.positive_count || 0),
        negative: Number(a.negative_count || 0),
      });
    });
  });

  const dynamicsCategories = uniqueCategoryIds.map((id) => {
    const c = byCategory.get(id) || { negative: 0, neutral: 0, positive: 0 };
    return {
      id,
      name: categoryName(id, categoryMap),
      negative: c.negative,
      neutral: c.neutral,
      positive: c.positive,
    };
  });

  const dates = periodLabel.includes('—')
    ? periodLabel.split('—').map((s) => s.trim())
    : [periodLabel, periodLabel];

  const charts: ExternalMonitoringChartsPayload = {
    sentimentDynamics: {
      dates: dates.length >= 2 ? dates : ['Начало периода', 'Конец периода'],
      positive: [positive, positive],
      negative: [-negative, -negative],
    },
    heatmap: {
      categories: heatmapCategories.length ? heatmapCategories : ['—'],
      categoryIds: heatmapCategoryIds.length ? heatmapCategoryIds : [''],
      counts: heatmapCounts.length ? heatmapCounts : [[0, 0, 0]],
      aspectsByCategoryId,
    },
    pyramid: {
      categories: pyramidCategories.length
        ? pyramidCategories
        : [{ id: '0', name: '—' }],
      aspects: pyramidAspects,
    },
    aspectsDynamics: {
      dates: dates.length >= 2 ? dates : ['Период', 'Период'],
      categories: dynamicsCategories,
    },
  };

  return { kpi, charts };
}

export { buildExternalMonitoringPeriod } from '../../../../../helpers/external-monitoring-period';
